import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { AxiosInstance } from 'axios';

import Loader from 'components/Loader';
import { getDateTime } from 'lib/utils';
import { PATH_ROUTES } from 'routes/path';
import ScanDetail from '../report/ScanDetail';
import { Button } from 'components/ui/button';
import CircularProgress from 'components/CircularProgress';
import { getCategorizedScanData } from 'lib/health-readings';

export const HEADING_MAPPER = {
  1: '1_2',
  2: '1_2',
  3: '3_4',
  4: '3_4',
  5: '5_6',
  6: '5_6',
  7: '7_8',
  8: '7_8',
  9: '9_10',
  10: '9_10',
};

interface HealthReadingProps {
  apiClient: AxiosInstance;
  reading_id?: string;
  profile_id: string;
}

const getHealthReading = async ({
  apiClient,
  profile_id,
  reading_id,
}: HealthReadingProps) => {
  return await apiClient.get('/web_health_reading', {
    withCredentials: true,
    params: {
      os: 'web',
      profile_id,
      ...(reading_id && { reading_id }),
      locale: 'en-us',
    },
  });
};

export default function FaceScanResult({
  apiClient,
}: {
  apiClient: AxiosInstance;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get('profile_id');

  const [wellnessIndex, setWellnessIndex] = useState(0);

  const { data: healthReadings, isFetching } = useQuery({
    queryKey: ['health-reading', profileId],
    queryFn: () => getHealthReading({ apiClient, profile_id: profileId! }),
    select: ({ data }) => data.data,
    enabled: !!profileId,
  });
  const readingId = healthReadings?.reading_data?.[0].reading_id!;

  const { data: readingData, isFetching: isReadingFetching } = useQuery({
    queryKey: ['qr-report-reading-by-id', readingId],
    queryFn: async () =>
      getHealthReading({
        apiClient,
        profile_id: profileId!,
        reading_id: readingId,
      }),
    enabled: !!readingId && readingId !== 'None',
    select: ({ data }) => data.data,
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (healthReadings?.reading_data.length) {
      const wellness = healthReadings.reading_data[0]?.WELLNESS_INDEX;
      setWellnessIndex(wellness);
    }

    return () => timeout && clearTimeout(timeout);
  }, [healthReadings]);

  const categorizedScanData = useMemo(
    () =>
      getCategorizedScanData({ healthReadings: readingData, selectedIndex: 0 }),
    [readingData]
  );

  const reportTime = useMemo(
    () =>
      healthReadings?.reading_data.length
        ? getDateTime(healthReadings?.reading_data[0].created_at)
        : null,
    [healthReadings]
  );

  if (isFetching || isReadingFetching) {
    return <Loader />;
  }

  if (!profileId) {
    return <Navigate to={PATH_ROUTES.LOGIN} replace />;
  }

  const onBack = () => {
    navigate(PATH_ROUTES.LOGIN);
  };

  return (
    <>
      <div className='relative w-full h-full p-4 px-6'>
        <Button
          variant='outline'
          className='h-8 px-6 text-sm rounded-full text-primary hover:text-primary border-primary'
          onClick={onBack}
        >
          <ArrowLeft className='w-4 h-4 mb-px mr-1' /> {t('go_to_login')}
        </Button>

        <div className='flex items-center justify-between px-4 pt-2 pb-8 mt-2 lg:pt-0 sm:pt-4 lg:pb-4 h-fit lg:h-32 sm:px-10 gap-x-4 bg-primary-dark'>
          <div className='text-white'>
            <h1 className='mt-2 text-lg font-semibold sm:text-2xl'>
              {t('wellness_score_heading')}: {wellnessIndex}
            </h1>
            <p className='text-sm font-semibold sm:text-base'>
              {t(
                `wellness_score_heading_for_${
                  HEADING_MAPPER[wellnessIndex as keyof typeof HEADING_MAPPER]
                }`
              )}
            </p>
            <p className='mt-1 text-sm leading-none'>
              {t(
                `wellness_score_subheading_for_${
                  HEADING_MAPPER[wellnessIndex as keyof typeof HEADING_MAPPER]
                }`
              )}
            </p>
          </div>
          <div className='size-20 shrink-0'>
            <CircularProgress score={wellnessIndex || 0} />
          </div>
        </div>

        <div className='-mt-4 bg-white border rounded-2xl border-primary'>
          {reportTime ? (
            <div className='flex justify-center p-2 pr-6 sm:justify-end'>
              <p className='text-sm font-medium'>
                {t('Report generated on')}: {reportTime.date} {reportTime.time}
              </p>
            </div>
          ) : null}

          <ScanDetail
            data={categorizedScanData}
            className='h-screen lg:h-[calc(100vh-21.5rem)] -mt-6'
            containerClassName='border-none pl-4 sm:pl-6'
          />
        </div>
      </div>
    </>
  );
}
